import { toRef, unref } from "vue";
import type { Reactive } from "vue";
import type { H3Event, EventHandlerRequest } from "h3";
import { useToast } from "vue-toast-notification";
import { useHconConfig } from "./hcon-config";
import { useHconDialog } from "./hcon-dialog";
import { useHconAuthInfo } from "./hcon-auth-info";
import { cu } from '~/utils/common-utils';
import { sha256 } from "js-sha256";
import type { NuxtApp } from "#app";
import type { IdentityVerificationContext } from "~/composables/hcon-identity-verification";
import { MEMBER_CODE } from '~/constants/hconMessageConstants';
import { usePopup } from '@/composables/hconUsePopup'
import SessionStorageSynchronizer from '~/utils/sync-sessionstorage';

// 전역 팝업
const {
  isTimeOut,
  isBefore3Min,
  isLoginPopupOpen,
  isLoggedIn
} = usePopup()

let trcodeindex         = 0;
let trcodeprefix        = new Date().getTime() + '@';
let refreshPromise:any;
let isShowLoginDialog   = false;

interface HconFetchBuilderByMethods {
    get:    <T>(uri: (Ref<string> | string)) => _HconFetchBuilder<T>;
    put:    <T>(uri: (Ref<string> | string)) => _HconFetchBuilder<T>;
    post:   <T>(uri: (Ref<string> | string)) => _HconFetchBuilder<T>;
    delete: <T>(uri: (Ref<string> | string)) => _HconFetchBuilder<T>;
}

interface FetchError {
    status: number;
    resultCode: number;
    resultMessage: string;
}

/**
 * useHconFetchBuilder()의 리턴 타입
 */
export interface HconFetchBuilder extends HconFetchBuilderByMethods {
    bff: () => HconFetchBuilderByMethods;
}


/**
 * HconFetchBuilder.build()에 의해 생성되는 Fetcher
 */
export interface HconFetcher<T> {
    isHconFetcher: boolean;
    /**
     * useFetch() 와 동일한 구조로 통신하여 UI와 반응형 관계로 엮을 수 있습니다.
     * UI렌더링이 완료 되기 전에 호출되어야 합니다.
     * @returns 
     */
    refResult: () => ReturnType<typeof useAsyncData<T | null>>;
    /**
     * $fetch와 동일한 구조로 통신합니다.
     * 따라서 useAsyncData에서 지원하는 일부 옵션은 무시됩니다.
     * UI에 반응형으로 연결할 필요없는 부분에서 사용합니다.
     * @returns 
     */
    execute: () => Promise<T>;
    /**
     * execute() 와 동일하지만 통신 결과 body가 아닌 Respose를 반환합니다.
     * @returns 
     */
    executeToRaw: () => ReturnType<typeof $fetch.raw<T>>;
    /**
     * 통신결과를 클리어 합니다.
     * refResult()로 사용되었을때만 동작 합니다.
     * 통신결과와 반응형으로 묶인 부분은 모두 클리어된 값을 기준으로 갱신됩니다.
     */
    clear: () => void;
    /**
     * 최종 통신 결과 data를 반환합니다.
     * refResult()로 사용되었을때만 동작 합니다.
     * @returns 
     */
    getData: () => Ref<(T | undefined)>;
    /**
     * 통신 상태를 반환합니다.
     * refResult()로 사용되었을때만 동작 합니다.
     * @returns 
     */
    getStatus: () => ReturnType<typeof useAsyncData<T>>['status'];
    /**
     * 최종 통신이 오류일때 오류결과를 반환합니다.
     * refResult()로 사용되었을때만 동작 합니다.
     * @returns 
     */
    getError: () => ReturnType<typeof useAsyncData<T>>['error'];
    /**
     * 재통신하고 데이터를 갱신합니다.
     * refResult()로 사용되었을때만 동작 합니다.
     * 통신결과와 반응형으로 묶인 부분은 모두 통신결과에 다라 재구성 됩니다.
     * @returns 
     */
    refresh: () => ReturnType<ReturnType<typeof useAsyncData<T>>['refresh']>;

    /**
     * fetcher에 결합되어 있는 H3Event를 반환합니다.
     * @returns 결합되어 있다면 H3Event 아니면 undefined
     */
    getH3Event: () => H3Event<EventHandlerRequest> | undefined;
}

class _HconFetchBuilder<T> {
    #baseProps:          {
        bff:                    boolean;
        uri:                    DynamicValue<string>;
        key?:                   string;
        preventErrorNotify?:    boolean;
        showInProgress?:        DynamicValue<boolean>;
        progressMessage?:       DynamicValue<string>;
        successMessage?:        DynamicValue<string>;
        failMessage?:           DynamicValue<string>;
        resultProvider?:        DynamicValue<any>;
        onDismiss?:             (isSuccess: boolean, message: string | undefined, result: T, error: any) => void;
        whenNotAuth?:           DynamicValue<any>;
    }
    #fetchOptions:       {
        method:         any;
        query?:         DynamicValue<any>,
        body?:          DynamicValue<any>,
        headers?:       any,
        timeout?:       number,
        cache?:         RequestCache,
    }
    #asyncDataOptions:   {
        server?:        boolean,
        lazy?:          boolean,
        immediate?:     boolean,
        default?:       () => any,
        transform?:     (data: any) => T,
        getCachedData?: (key: string, nuxtApp: NuxtApp) => T,
        pick?:          Array<T extends T ? keyof T extends string ? keyof T : never : never>,
        watch?:         any[], 
        deep?:          boolean,
        dedupe?:        "cancel" | "defer" | undefined,
    }
    #toast: ReturnType<typeof useToast>;
    #config: ReturnType<typeof useHconConfig>;
    #authinfoHandler: ReturnType<typeof useHconAuthInfo> | undefined;

    #h3event?: H3Event<EventHandlerRequest>;
    #isMobile?: boolean;
    #userVerification?: Ref<IdentityVerificationContext>;

    constructor(bff: boolean
        , uri: (Ref<string> | string)
        , method: string
        , toast: ReturnType<typeof useToast>
        , config: ReturnType<typeof useHconConfig>
        , authinfoHandler?: ReturnType<typeof useHconAuthInfo>
        , h3event?: H3Event<EventHandlerRequest>
        , isMobile?: boolean
        , userVerification?: Ref<IdentityVerificationContext>) {
        this.#baseProps         = {
            bff: bff,
            uri: toRef(uri),
        }
        this.#fetchOptions      = {
            method: method,
        }
        this.#asyncDataOptions  = {};
        this.#h3event           = h3event;
        this.#toast             = toast;
        this.#config            = config;
        this.#authinfoHandler   = authinfoHandler;
        this.#isMobile          = isMobile;
        this.#userVerification  = userVerification;
    }

    /**
     * useAsyncData 와 같이 key를 지정할 수 있습니다.
     * key를 지정하면 clearNuxtData(key), refreshNuxtData(key) 등의 Next 제공 함수를 사용하여 컨트롤 할 수 있습니다.
     * @param key 
     * @returns 
     */
    key(key?: string) {
        this.#baseProps.key      = key;
        return this;
    }

    /**
     * 클라이언트 사이드에서만 통신 처리 합니다.
     * useAsyncData의 옵션 server로 false 지정한것과 같습니다.
     * @returns 
     */
    clientOnly() {
        this.#asyncDataOptions.server    = false;
        return this;
    }

        /**
     * 로그인하지 않은 사용자의 요청을 처리할 대안을 설정합니다.
     * 주어지는 대안의 실제값이 string 이면  대체API로 사용되고
     * Object이면 통신을 생략한 결과로 사용되며,
     * 대안이 지정되지 않거나 undefined로 명시되면, 통신을 생략하고 undefined를 결과로 설정합니다.
     * 이 옵션은 로그인 하지 않았을때만 이며, 세션만료는 처리 대상이 아닙니다.
     * 이 옵션이 설정되면  자동으로 clientOnly()가 설정됩니다.
     * @param alternateWhenNotAuth 
     * @returns 
     */
        whenNotAuth(alternateWhenNotAuth?:DynamicValue<any>) {
            this.#baseProps.whenNotAuth     = alternateWhenNotAuth;
            return this.clientOnly();
    
        }

    /**
     * 통신 공통 UI처리 과정에서 에러메세지 표시를 하지 않도록 억제 시킵니다.
     * @returns 
     */
    preventErrorNotify() {
        this.#baseProps.preventErrorNotify   = true;
        return this;
    }

    /**
     * 통신 진행 중에는 메세지를 표시하고, 전체 화면을 dimm 처리하여 클릭을 방지합니다.
     * 처리중 추가 조작을 방지해야 하는 중요한 처리 과정일 경우 설정하십시오.
     * @param message 
     * @returns 
     */
    progressMessage(message: Ref<string> | string) {
        this.#baseProps.progressMessage  = toRef(message);
        return this;
    }

    /**
     * 정상적인 통신 완료시 표시할 메세지를 지정합니다.
     * @param message 
     * @returns 
     */
    successMessage(message: Ref<string> | string) {
        this.#baseProps.successMessage  = toRef(message);
        return this;
    }

    /**
     * 통신 오류시 표시할 메세지를 지정합니다.
     * @param message
     */
    failMessage(message: Ref<string> | string) {
        this.#baseProps.failMessage  = toRef(message);
        return this;
    }

    /**
     * 호출 URI에 QueryString으로 추가할 내용을 VO로 지정합니다.
     * @param query 
     * @returns 
     */
    query(query?: any) {
        if (query == null) {
            this.#fetchOptions.query = undefined;
        } else {
            this.#fetchOptions.query = toRef(query);
        }
        return this;
    }

    /**
     * 통신 body로 전송할 VO를 지정합니다.
     * @param body 
     * @returns 
     */
    body(body?: any) {
        if (body == null) {
            this.#fetchOptions.body  = undefined;
        } else  {
            this.#fetchOptions.body = toRef(body);
        }
        return this;
    }

    /**
     * 통신 요청 해더로 추가할 내용을 지정합니다.
     * @param headers 
     * @returns 
     */
    headers(headers?: any) {
        if (headers != null) {
            if (this.#fetchOptions.headers == null) {
                this.#fetchOptions.headers    = headers;
            } else {
                this.#fetchOptions.headers    = {...this.#fetchOptions.headers, ...headers}
            }
        }
        return this;
    }

    /**
     * 요청 타임아웃을 지정합니다.
     * $fetch의 옵션에 지정한는 타임아웃과 동일합니다.
     * @param timeout
     * @returns 
     */
    timeout(timeout?: number) {
        this.#fetchOptions.timeout    = timeout;
        return this;
    }

    /**
     * 통신 결과에 대한 캐시 정책을 지정합니다.
     * js fetch의 옵션에 지정하는 내용과 동일합니다.
     * @param cache 
     * @returns 
     */
    cache(cache?: RequestCache) {
        this.#fetchOptions.cache  = cache;
        return this;
    }

    /**
     * useAsyncData의 옵션 lazy와 동일
     * @param lazy 
     * @returns 
     */
    lazy(lazy?: boolean) {
        this.#asyncDataOptions.lazy   = lazy;
        return this;
    }

    /**
     * useAsyncData의 옵션 immediate과 동일
     * @param immediate
     * @returns 
     */
    immediate(immediate?: boolean) {
        this.#asyncDataOptions.immediate  = immediate;
        return this;
    }

    /**
     * useAsyncData의 옵션 default와 동일
     * @param defaultData
     * @returns 
     */
    defaultData(defaultData?: () => any) {
        this.#asyncDataOptions.default    = defaultData;
        return this;
    }

    /**
     * useAsyncData의 옵션 transform과 동일
     * @param fn 
     * @returns 
     */
    transform<E>(fn: (data: any) => T) {
        this.#asyncDataOptions.transform  = fn;
        return this;
    }

    /**
     * useAsyncData의 옵션 getCachedData와 동일
     * @param fn 
     * @returns 
     */
    cacheDataGetter(fn: () => T) {
        this.#asyncDataOptions.getCachedData  = fn;
        return this;
    }

    /**
     * useAsyncData의 옵션 pick와 동일
     * @param pick 
     * @returns 
     */
    pick(...pick: Array<T extends T ? keyof T extends string ? keyof T : never : never>) {
        if (pick != null) {
            if (this.#asyncDataOptions.pick == null) {
                this.#asyncDataOptions.pick   = pick;
            } else {
                this.#asyncDataOptions.pick   = [...this.#asyncDataOptions.pick, ...pick];
            }
        }
        return this;
    }

    /**
     * useAsyncData의 옵션 watch와 동일
     * @param items 
     * @returns 
     */
    watch(...items: any[]) {
        if (items != null) {
            if (this.#asyncDataOptions.watch == null) {
                this.#asyncDataOptions.watch  = items;
            } else {
                this.#asyncDataOptions.watch = [...this.#asyncDataOptions.watch, items];
            }
        }
        return this;
    }

    /**
     * useAsyncData의 옵션 deep과 동일
     * @param items 
     * @returns 
     */
    deep(deep?: boolean) {
        this.#asyncDataOptions.deep   = deep;
        return this;
    }

    /**
     * useAsyncData의 옵션 dedupe을 cancel로 설정
     * @returns 
     */
    dedupeCancel() {
        this.#asyncDataOptions.dedupe = 'cancel';
        return this;
    }

    /**
     * useAsyncData의 옵션 dedupe을 defer로 설정
     * @returns 
     */
    dedupeDefer() {
        this.#asyncDataOptions.dedupe = 'defer';
        return this;
    }

    /**
     * 실제 통신을 처리하지 않고 결과를 반환하게 만듭니다.
     * 개발용 Mock 혹은 조건에 의해 통신과정 없이 정해둔 데이터를 사용하도록 할때 사용합니다.
     * @param provider
     * @returns 
     */
    resultProvider(provider: Ref<any>) {
        this.#baseProps.resultProvider  = provider;
        return this;
    }

    /**
     * 에러/성공 메세지가 close 되고 난 뒤 후속 처리할 내용을 펑션으로 지정합니다.
     * @param fn 
     * @returns 
     */
    onDismiss(fn: (isSuccess: boolean, message: string | undefined, result: T, error: any) => void) {
        this.#baseProps.onDismiss  = fn;
        return this;
    }

    /**
     * 구성된 옵션이 적용된 처리 객체를 만들어 냅니다.
     * @returns 
     */
    build() {
        const   commited = {
            baseProps:          {...this.#baseProps},
            fetchOptions:       {...this.#fetchOptions},
            asyncDataOptions:   {...this.#asyncDataOptions},
        }
        const passthroughHeaders    = {} as {
            xForwaredFor?: string | null,
            xForwaredHost?: string | null,
            host?: string | null,
            authorization?: string | null,
            xClientDomain?: string | null,
            xUserVerfication?: string | null,
            xAdultVerfication?: string | null,
        };

        let trindex                 = 0;
        let resultedRef: ReturnType<typeof useAsyncData<T | null>>;

        const authinfoHandler       = this.#h3event? undefined : useHconAuthInfo();

        if (this.#h3event) {
            passthroughHeaders.xForwaredHost    = getRequestHeader(this.#h3event, 'X-Forwarded-Host');
            passthroughHeaders.xForwaredFor     = getRequestHeader(this.#h3event, 'X-Forwarded-For');
            passthroughHeaders.host             = getRequestHeader(this.#h3event, 'Host');
            passthroughHeaders.authorization    = getRequestHeader(this.#h3event, 'Authorization');
            passthroughHeaders.xClientDomain    = getRequestHeader(this.#h3event, 'X-Client-Domain');
            passthroughHeaders.xUserVerfication = getRequestHeader(this.#h3event, 'X-User-Verification');
            passthroughHeaders.xAdultVerfication = getRequestHeader(this.#h3event, 'X-Adult-Verification');
        } else {
            passthroughHeaders.xForwaredHost    = useRequestHeader('X-Forwarded-Host');
            passthroughHeaders.xForwaredFor     = useRequestHeader('X-Forwarded-For');
            passthroughHeaders.host             = useRequestHeader('Host');
        }

        const   checkResultedRef = () => {
            if (resultedRef == null) {
                throw new Error('refResult()가 수행된 적이 있을때만 사용할 수 있습니다.');
            }
        }

        const dialog : ReturnType<typeof useHconDialog> | null = this.#h3event === undefined ? useHconDialog() : null;
        let dialogId: string | undefined; 

        const handleStart   = (trcode: string, uri: string, options: any, showInProgress?: boolean, progressMessage?: string) => {
            const paths                 = uri.split('/');

            if (dialog) {
                if (showInProgress !== undefined) {
                    dialogId    = dialog.inProgress(showInProgress);
                } else if (progressMessage) {
                    dialogId    = dialog.dimm(progressMessage);
                }
            }
            //options.credentials = 'include'
            if (options.headers === undefined) {
                options.headers = {};
            }
            options.headers['X-System-type'] = 'HCON_FO';
            if (options.headers['Accept'] === undefined) {
                options.headers['Accept']   = "application/json";
            }
            if (options.headers['Content-Type'] === undefined) {
                options.headers['Content-Type']   = options.body instanceof Blob ? "application/octet-stream" : "application/json";
            }
            if (passthroughHeaders.xForwaredFor) {
                options.headers['X-Forwarded-For'] = passthroughHeaders.xForwaredFor;
            }
            if (passthroughHeaders.authorization) {
                options.headers['Authorization'] = passthroughHeaders.authorization;
            }
            if (this.#baseProps.bff) {
                if (passthroughHeaders.xForwaredHost) {
                    options.headers['X-Forwarded-Host'] = passthroughHeaders.xForwaredHost;
                }
            }
            if (passthroughHeaders.xUserVerfication) {
                options.headers['X-User-Verification'] = passthroughHeaders.xUserVerfication;
            }
            if (passthroughHeaders.xAdultVerfication) {
                options.headers['X-Adult-Verification'] = passthroughHeaders.xUserVerfication;
            }
            const authInfo  = this.#authinfoHandler?.getIfPossible();
            if (authInfo) {
                const now                   = new Date().getTime();
                const accessExpireTime      = authInfo.lastAccess + (authInfo.accessTimeout*3600);
                
                if (now < accessExpireTime) {
                    this.#authinfoHandler?.set(authInfo);
                    
                    const { $start27Timer, $stopTimer } = useNuxtApp()
                    $stopTimer()
                    $start27Timer(authInfo.lastAccess as number)
                    options.headers['Authorization']    = authInfo.accessToken;
                    options.headers['Refresh-Token']    = authInfo.refreshToken;
                    options.headers['X-Client-Domain']  = passthroughHeaders.xClientDomain ? passthroughHeaders.xClientDomain
                                                            : (this.#h3event ? this.#h3event.context.clientDomain : useNuxtApp().$clientDomain);
                            
                    // if (this.#baseProps.bff) {
                    //     options.headers['refresh_token'] = authInfo.refreshToken;
                    // }

                    // if (this.#userVerification?.value?.verificationId != null) {
                    //     options.headers['X-User-Verification'] = this.#userVerification.value.verificationId;
                    // }
                    // if (this.#userVerification?.value?.adultVerificationId != null) {
                    //     options.headers['X-Adult-Verification'] = this.#userVerification.value.adultVerificationId;
                    // }
                }
                // } else if (now > accessExpireTime){
                //     this.#authinfoHandler?.clear();
                //     if (this.#h3event == null) {
                //         nextTick(() => {
                //             isLoggedIn.value = false;
                //             useHconAuthInfo().clear();
                //             isTimeOut.value = true;
                //         });
                //         return null;
                //     }
                // }
            }

            if (import.meta.server) {
                console.log(`[hcon-api#${trcode}] ${uri} started`);
            }
            return uri;
        };

        const handleRefresh = (trcode: string, actualOptions?: any): Promise<any | null> => {
            if (refreshPromise) {
                console.debug(`[hcon-api#${trcode}] 이미 처리중인 refresh가 있어 요청을 통합합니다.`);
                return refreshPromise;
            }
            const rememberMeToken   = import.meta.client ? SessionStorageSynchronizer.getItem("common.auth.rememberMe") : null;
            if (actualOptions.headers['Authorization'] !== undefined|| rememberMeToken != null) {
                console.debug(`[hcon-api#${trcode}] 토큰이 만료되어 refresh 시도합니다.`);
                const authInfo      = authinfoHandler?.getIfPossible();
                const refreshToken  = authInfo?.refreshToken;
                if (refreshToken|| rememberMeToken != null) {
                    const refreshFetchHeaders:any    = {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                    }
                    if (rememberMeToken != null) {
                        refreshFetchHeaders['X-Remember-Me'] = rememberMeToken;
                    }

                    refreshPromise = $fetch<AjaxResult<AuthInfo>>('/api/v1/member/auth/refresh-token', {
                        baseURL: useHconConfig().api.baseUrl,
                        method: 'POST',
                        //credentials: 'include',
                        headers: refreshFetchHeaders,
                        body: JSON.stringify({
                            accessToken: actualOptions.headers['Authorization'],
                            refreshToken: refreshToken
                        })
                    }).then(refreshRes => {
                        try {
                            if (refreshRes.resultCode == "200") {

                                console.debug(`[hcon-api#${trcode}] 토큰이 갱신되었습니다.`,  refreshRes.data.accessToken);
                                if(authInfo) {
                                    authInfo.accessToken    = refreshRes.data.accessToken;
                                    authInfo.refreshToken   = refreshRes.data.refreshToken;
                                    authInfo.ulid4legacy    = refreshRes.data.ulid4legacy;
                                    authinfoHandler?.set(authInfo);
                                    return authInfo.accessToken;
                                }
                                
                            }
                        } finally {
                            refreshPromise = undefined;
                        }
                    }).catch(err => {
                        try {
                            throw err;
                        } finally {
                            refreshPromise = undefined;
                        }
                    });
                    return refreshPromise;
                } else {
                    console.warn(`[hcon-api#${trcode}] refresh 토근을 획득할수 없어 인증토큰 재발급 처리 할 수 없습니다.`);
                }
            }
            return cu.promiseOf(null);
        }

        const handleEnd     = (actualOptions: any, trcode: string, result: any, uri: string, error: FetchError | undefined, successMessage?: string, failMessage?: string) => {
            if (error) {
                if (error.status == 0 || error.status >= 500) {
                    console.error(`[hcon-api#${trcode}] ${uri} end with error`, error);
                } else if (error.status == 401 || error.resultCode == 401) {
                    if (dialog && !isShowLoginDialog) {
                        dialog.confirm(actualOptions.headers['Authorization'] ? "세션이 만료되었습니다. 다시 로그인 하시겠습니까?" : "세션이 필요한 요청입니다. 로그인 하시겠습니까?", (choice) => {
                            if (choice == true) {
                                isLoginPopupOpen.value = true;
                            } else {
                                isLoggedIn.value = false;
                                useHconAuthInfo().clear();
                                const { $stopTimer } = useNuxtApp()
                                $stopTimer();
                                navigateTo({path: '/pc/product/main'}, {replace:true});
                                localStorage.setItem('auth:event', JSON.stringify({
                                    type: 'LOGOUT',
                                    at: Date.now()
                                }));
                            }
                        });
                        //isShowLoginDialog   = true;
                    }
                }
                if (import.meta.client && commited.baseProps.preventErrorNotify != true) {
                    const errorMessage  = error.resultMessage || error.toString();
                    if (dialog && failMessage) {
                        dialog.alert("처리실패.\n" + failMessage, () => {
                            if (commited.baseProps.onDismiss) {
                                commited.baseProps.onDismiss(false, failMessage, result, error);
                            }
                        });
                    } else {
                        if (import.meta.dev) {
                            const message       = failMessage ? failMessage + "\n" + errorMessage : errorMessage;
                            this.#toast.error(message, {
                                onDismiss: () => {
                                    if (commited.baseProps.onDismiss) {
                                        commited.baseProps.onDismiss(false, failMessage, result, error);
                                    }
                                }
                            })
                        } else {
                            if (commited.baseProps.onDismiss) {
                                commited.baseProps.onDismiss(false, failMessage, result, error);
                            }
                        }
                    }
                }
            } else {
                if (import.meta.server) {
                    console.log(`[hcon-api#${trcode}] ${uri} end with success`);
                }
                //console.debug(`[hcon-api#${trcode}] end with success`, result);
                if (import.meta.client) {
                    if (successMessage) {
                        if (dialog && successMessage) {
                            dialog.alert(successMessage, () => {
                                if (commited.baseProps.onDismiss) {
                                    commited.baseProps.onDismiss(true, successMessage, result, error);
                                }
                            });
                        } else {
                            if (import.meta.dev) {
                                this.#toast.success(successMessage, {
                                    duration: 5000,
                                    onDismiss: () => {
                                        if (commited.baseProps.onDismiss) {
                                            commited.baseProps.onDismiss(true, successMessage, result, error);
                                        }
                                    }
                                });
                            } else {
                                if (commited.baseProps.onDismiss) {
                                    commited.baseProps.onDismiss(true, successMessage, result, error);
                                }
                            }
                        }
                    }
                }
            }
        };

        const doCall    = async (trcodebase:string, raw?: boolean) => {
            const callBaseUrl = (() => {
                let rv;
                if (commited.baseProps.bff) {
                    rv  = this.#config.bff.baseUrl;
                    // if (import.meta.server) {
                    //     console.log("config bff base-url ", rv);
                    // }
                    if (cu.isEmpty(rv) && this.#h3event) {
                        const host = passthroughHeaders.xForwaredHost ?? passthroughHeaders.host;
                        rv  = `https://${host}`;

                        // if (import.meta.server) {
                        //     console.log("actual bff base-url ? ", rv);
                        // }
                    }
                } else {
                    rv  = this.#config.api.baseUrl;
                }
                return rv;
            })();

            const actualOptions = {...commited.fetchOptions,
                baseURL: callBaseUrl,
                query: cu.actualValue(commited.fetchOptions.query),
                body: cu.actualValue(commited.fetchOptions.body)   
            }
            const authInfo  = this.#authinfoHandler?.getIfPossible();

            let result: any;
            let error:FetchError | undefined;
            let trcode  = `${trcodebase}.` + (++trindex);
            let uri:string  = '';
            try {
                const whenNotAuth   = cu.actualValue(commited.baseProps.whenNotAuth);
                const preuri        = (() => {
                    if (authInfo == null && typeof(whenNotAuth) == 'string') {
                        return whenNotAuth;
                    }
                    const actualUri = cu.actualValue(commited.baseProps.uri);
                    if (actualUri && actualUri.startsWith(actualOptions.baseURL)) {
                        return actualUri.substring(actualOptions.baseURL.length);
                    } else {
                        return actualUri;
                    }
                })();
                if (commited.baseProps.resultProvider) {
                    result  = cu.actualValue(commited.baseProps.resultProvider);
                } else if (authInfo == null && whenNotAuth !== undefined && typeof(whenNotAuth) != 'string') {
                    result  = whenNotAuth;
                }
                if (result === undefined) {
                    if (preuri == null) {
                        throw { status: 0, rcode: 'REQUEST_FAIL', message: '통신 대상 URI가 지정되지 않았습니다.'};
                    }
                    const actualUri = handleStart(trcode, preuri, actualOptions, cu.actualValue(commited.baseProps.showInProgress), cu.actualValue(commited.baseProps.progressMessage));
                    if (actualUri == null) {
                        return;
                    }
                    uri = actualUri;
                    let resultPromise   = $fetch.raw(uri, actualOptions)
                            .then(async (res) => {
                                const data: any = res?._data;
                                if(data?.resultCode == 400 || data?.resultCode == '400') {
                                    const {showConfirm} = usePopup();

                                    const result = await showConfirm({
                                        title: "중복 로그인 안내",
                                        message: HconCommonUtils.format.message(MEMBER_CODE.ALT_HCON_9140),
                                        showCancelButton: false,
                                      })

                                    if(result){
                                        SessionStorageSynchronizer.removeItem('common.auth.rememberMe')
                                        sessionStorage.removeItem('common.auth.info')
                                        
                                        setTimeout(() => {
                                            isLoggedIn.value = false;
                                            useHconAuthInfo().clear();
                                            navigateTo({path: '/pc/product/main'}, {replace:true});
                                            localStorage.setItem('auth:event', JSON.stringify({
                                                type: 'LOGOUT',
                                                at: Date.now()
                                            }));
                                        }, 1500)
                                    } else {
                                        SessionStorageSynchronizer.removeItem('common.auth.rememberMe')
                                        sessionStorage.removeItem('common.auth.info')
                                        
                                        setTimeout(() => {
                                            isLoggedIn.value = false;
                                            useHconAuthInfo().clear();
                                            navigateTo({path: '/pc/product/main'}, {replace:true});
                                            localStorage.setItem('auth:event', JSON.stringify({
                                                type: 'LOGOUT',
                                                at: Date.now()
                                            }));
                                        }, 1500)
                                    }
                                }

                                if(data?.resultCode == 401 || data?.resultCode == '401') {
                                    throw {status:401, _data: data}
                                }

                                return res;
                            }).catch(async err => {
                                if (err.status == 401 && this.#h3event == null) {
                                    const newToken  = await handleRefresh(trcode, actualOptions);
                                    if (newToken == null) {
                                        const result    = err._data as any;
                                        throw { status: 401, data: {resultCode: result?.resultCode ? result?.resultCode : 401, resultMessage: result?.resultMessage ? result?.resultMessage : "로그인이 필요합니다."}};
                                    } else {
                                        actualOptions.headers['Authorization'] = newToken;
                                        return $fetch.raw(uri, actualOptions);
                                    }
                                }
                                throw err;
                            });
    
                    result = await resultPromise.then(res => {
                        if (import.meta.client) {
                            if ( res ) {
                             const resRememberMe = res.headers.get("x-remember-me");
                             if (resRememberMe == '') {
                                SessionStorageSynchronizer.removeItem('common.auth.rememberMe');
                            } else if (resRememberMe != null) {
                                SessionStorageSynchronizer.setItem('common.auth.rememberMe', resRememberMe);
                            }
                            }
                        }
                       
                        if (raw) {
                            return res;
                        } else {

                            if (res?.status == undefined || res.status == undefined || res.status < 200 || res.status >= 400 || res._data == undefined) {

                                const result    = res?._data as any;
                                const status    = res?.status ? res.status : 0; 
                                throw { status: status, data: {resultCode: result?.resultCode ? result?.resultCode : status.toString(),  resultMessage: result?.resultMessage ? result?.resultMessage: status.toString(), data: res?._data}};
                            }
                            return res._data;
                        }
                    });
                }
                if (result?.resultCode !== undefined && result.resultCode != '200') {
                    throw { status: result.resultCode, data: {resultCode: result.resultCode, resultMessage: result.resultMessage}};
                }
        } catch (err: any) {
                if (err.data === undefined) {
                    error   = { status: 0, resultCode: 0, resultMessage: err.toString()};
                } else if (err.data?.resultCode !== undefined)  {
                    error   = { status: err.status, resultCode: err.data.resultCode, resultMessage: err.data.resultMessage }
                } else if (err.data?.data?.resultCode !== undefined)  {
                    error   = { status: err.status, resultCode: err.data.data.resultCode, resultMessage: err.data.data.resultMessage }
                } else if (err.resultCode === undefined)  {
                    error   = { status: err.status, resultCode: err.status.toString(), resultMessage: "HTTP-" + err.status }
                }
                if (typeof error?.status === 'string') {
                    error.status    = parseInt(error.status);
                }
                err = {...error, ...err};
                if (err && err.status >= 600) {
                    err.status    = 500;
                }
                if (err.message === undefined) {
                    err.message = err.resultMessage;
                }
                throw err;
            } finally {
                try {
                    handleEnd(actualOptions, trcode, result, uri, error, cu.actualValue(commited.baseProps.successMessage), cu.actualValue(commited.baseProps.failMessage));
                } finally {
                    if (dialog && dialogId !== undefined) {
                        dialog.close(dialogId);
                    }
                }
            }
            return result;
        }

        const getActualKey = () => {
            if (commited.baseProps.key) {
                return commited.baseProps.key;
            }
            const actualUri     = cu.actualValue(commited.baseProps.uri);
            const actualQuery   = cu.actualValue(commited.fetchOptions.query);
            const querySig      = actualQuery === undefined ? '' : JSON.stringify(actualQuery);
            return 'unnamed-' + sha256(`${commited.fetchOptions.method}\\\\${commited.baseProps.bff}\\\\${actualUri}\\\\${querySig}`);
        }

        const fetchHandler: HconFetcher<T> = {
            isHconFetcher: true,
            refResult: () => {
                if (resultedRef !== undefined) {
                    var callerHint  = commited.baseProps.key ? `fetch key : ${commited.baseProps.key}` : `call api : ${cu.actualValue(commited.baseProps.uri)}`;
                    throw Error(`refResult()는 한번만 호출 될 수 있습니다. - ${callerHint}`);
                }
                const key   = getActualKey();

                resultedRef = useAsyncData<T>(key, () => doCall(`tr-${key}`), commited.asyncDataOptions);
                return resultedRef;
            },
            execute: () => {
                if(isLoginPopupOpen.value) {
                    return cu.promiseOf(undefined)
                }
                const trcodeBase    = 'tr-' + getActualKey();
                if (commited.asyncDataOptions.server == false && import.meta.server) {
                    console.warn(new Error(`[hcon-api#${trcodeBase}] clientOnly 설정된 fetcher가 server-size에서 execute() 호출 되었습니다.`));
                    return cu.promiseOf(undefined);
                }
                return doCall(trcodeBase).then(result => {
                    if (commited.asyncDataOptions.transform) {
                        result  = commited.asyncDataOptions.transform(result);
                    }
                    if (commited.asyncDataOptions.pick) {
                        let   pickedResult: any = {};
                        for (const key of commited.asyncDataOptions.pick) {
                            pickedResult[key]   = result[key];
                        }
                        result  = pickedResult;
                    }
                    return result;
                });
            },
            executeToRaw: () => {
                const trcodeBase    = 'tr-' + getActualKey();
                if (commited.asyncDataOptions.server == false && import.meta.server) {
                    console.warn(new Error(`[hcon-api#${trcodeBase}] clientOnly 설정된 fetcher가 server-size에서 executeToRaw() 호출 되었습니다.`));
                    return cu.promiseOf(undefined);
                }
                return doCall(trcodeBase, true).then(result => {
                    if (commited.asyncDataOptions.transform) {
                        result._data  = commited.asyncDataOptions.transform(result._data);
                    }
                    if (commited.asyncDataOptions.pick) {
                        let   pickedResult: any = {};
                        for (const key of commited.asyncDataOptions.pick) {
                            pickedResult[key]   = result._data[key];
                        }
                        result._data  = pickedResult;
                    }
                    return result;

                });
            },
            refresh: (): ReturnType<typeof resultedRef.refresh> => {
                checkResultedRef();
                return resultedRef.refresh();
            },
            clear: () => {
                checkResultedRef();
                resultedRef.clear();
            },
            getData: (): (Ref<(T | undefined)>)=> {
                checkResultedRef();
                return resultedRef.data as Ref<(T | undefined)>;
            },
            getError: () => {
                checkResultedRef();
                return resultedRef.error;
            },
            getStatus: () => {
                checkResultedRef();
                return resultedRef.status;
            },
            getH3Event: () => {
                return this.#h3event;
            }
        }
        return fetchHandler;
    }
}

interface HconFetchBuilderByMethods {
    get:    <T>(uri: (Ref<string> | string)) => _HconFetchBuilder<T>;
    put:    <T>(uri: (Ref<string> | string)) => _HconFetchBuilder<T>;
    post:   <T>(uri: (Ref<string> | string)) => _HconFetchBuilder<T>;
    delete: <T>(uri: (Ref<string> | string)) => _HconFetchBuilder<T>;
}

export interface HconFetchBuilder extends HconFetchBuilderByMethods {
    bff: () => HconFetchBuilderByMethods;
}


export const useHconFetchBuilder = (h3event?: H3Event<EventHandlerRequest>) : HconFetchBuilder => {
    const toast                 = useToast();
    const config                = useHconConfig(h3event);
    const authinfoHandler       = h3event ? undefined : useHconAuthInfo();
    const isMobile              = h3event ? undefined : useDevice().isMobile??'';
    const userVerification      = h3event ? undefined : useHconIdentityVerification().getContext();
    return {
        bff: () => {
            return {
                get:    <T>(uri: (Ref<string> | string)) => new _HconFetchBuilder<T>(true, uri, 'GET', toast, config, authinfoHandler, h3event, isMobile, userVerification),
                put:    <T>(uri: (Ref<string> | string)) => new _HconFetchBuilder<T>(true, uri, 'PUT', toast, config, authinfoHandler, h3event, isMobile, userVerification),
                post:   <T>(uri: (Ref<string> | string)) => new _HconFetchBuilder<T>(true, uri, 'POST', toast, config, authinfoHandler, h3event, isMobile, userVerification),
                delete: <T>(uri: (Ref<string> | string)) => new _HconFetchBuilder<T>(true, uri, 'DELETE', toast, config, authinfoHandler, h3event, isMobile, userVerification),
        
            }
        },
        get:    <T>(uri: (Ref<string> | string)) => new _HconFetchBuilder<T>(false, uri, 'GET', toast, config, authinfoHandler, h3event, isMobile, userVerification),
        put:    <T>(uri: (Ref<string> | string)) => new _HconFetchBuilder<T>(false, uri, 'PUT', toast, config, authinfoHandler, h3event, isMobile, userVerification),
        post:   <T>(uri: (Ref<string> | string)) => new _HconFetchBuilder<T>(false, uri, 'POST', toast, config, authinfoHandler, h3event, isMobile, userVerification),
        delete: <T>(uri: (Ref<string> | string)) => new _HconFetchBuilder<T>(false, uri, 'DELETE', toast, config, authinfoHandler, h3event, isMobile, userVerification),
    }
}
